"use client";
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Progress } from './progress';

export type QuestionnaireAnswer=string|string[];
export type QuestionnaireAnswers=Record<string,QuestionnaireAnswer>;
export interface QuestionnaireQuestion {
  id:string;
  title:string;
  description?:string;
  options?:(string|{label:string;value:string})[];
  multiple?:boolean;
  optional?:boolean;
  allowCustom?:boolean;
  placeholder?:string;
  validate?:(answer:QuestionnaireAnswer|undefined,answers:QuestionnaireAnswers)=>string|undefined;
  when?:(answers:QuestionnaireAnswers)=>boolean;
}
export interface QuestionnaireProps {
  questions:QuestionnaireQuestion[];
  answers?:QuestionnaireAnswers;
  defaultAnswers?:QuestionnaireAnswers;
  onAnswersChange?:(answers:QuestionnaireAnswers)=>void;
  currentIndex?:number;
  defaultCurrentIndex?:number;
  onCurrentIndexChange?:(index:number)=>void;
  onComplete?:(answers:QuestionnaireAnswers)=>void;
  submitLabel?:string;
  className?:string;
  disabled?:boolean;
}
export function Questionnaire({questions,answers:controlledAnswers,defaultAnswers={},onAnswersChange,currentIndex,defaultCurrentIndex=0,onCurrentIndexChange,onComplete,submitLabel='Complete',className,disabled}:QuestionnaireProps){
  const [local,setLocal]=React.useState(defaultAnswers);
  const [localIndex,setIndex]=React.useState(defaultCurrentIndex);
  const [error,setError]=React.useState('');
  const [complete,setComplete]=React.useState(false);
  const answers=controlledAnswers??local;
  const visible=questions.filter(q=>!q.when||q.when(answers));
  const index=Math.max(0,Math.min(visible.length-1,currentIndex??localIndex));
  const question=visible[index];
  const name=React.useId();
  function navigate(next:number){if(currentIndex===undefined)setIndex(next);onCurrentIndexChange?.(next);setError('');}
  function answer(value:QuestionnaireAnswer){const next={...answers,[question.id]:value};if(controlledAnswers===undefined)setLocal(next);onAnswersChange?.(next);setError('');}
  function advance(skip=false){
    if(disabled)return;
    const value=answers[question.id];
    if(!skip){const error=question.validate?.(value,answers)||(!question.optional&&(!value||value.length===0)?'Choose an answer to continue.':'');if(error){setError(error);return;}}
    if(index===visible.length-1){setComplete(true);onComplete?.(answers);}else navigate(index+1);
  }
  if(!question)return <div role="status">No questions to answer.</div>;
  if(complete)return <div className="questionnaire-done" role="status"><strong>All set.</strong><p>Your answers are ready.</p><Button variant="outline" onClick={()=>{setComplete(false);navigate(0);}}>Review answers</Button></div>;
  const selected=answers[question.id];
  const options=question.options?.map(o=>typeof o==='string'?{value:o,label:o}:o)??[];
  return <section data-slot="questionnaire" className={cn('freeui-questionnaire',className)} onKeyDown={e=>{if(disabled||(e.target instanceof HTMLInputElement&&!['radio','checkbox'].includes(e.target.type))||e.target instanceof HTMLTextAreaElement)return;const n=Number(e.key)-1;if(n>=0&&n<options.length){e.preventDefault();const v=options[n].value;answer(question.multiple?(Array.isArray(selected)?selected.includes(v)?selected.filter(a=>a!==v):[...selected,v]:[v]):v);}}}>
    <div className="questionnaire-step">Question {index+1} of {visible.length}<span>{Math.round(index/visible.length*100)}%</span></div><Progress value={index/visible.length*100}/>
    <h3>{question.title}</h3>{question.description&&<p>{question.description}</p>}
    <div role={question.multiple?'group':'radiogroup'} aria-label={question.title}>{options.map((option,n)=><label key={option.value} className="questionnaire-option"><input aria-label={option.label} disabled={disabled} name={name} type={question.multiple?'checkbox':'radio'} checked={Array.isArray(selected)?selected.includes(option.value):selected===option.value} onChange={()=>answer(question.multiple?(Array.isArray(selected)?selected.includes(option.value)?selected.filter(v=>v!==option.value):[...selected,option.value]:[option.value]):option.value)}/><span>{option.label}</span><kbd aria-hidden="true">{n+1}</kbd></label>)}</div>
    {(question.allowCustom||options.length===0)&&<Input aria-label={`Custom answer to ${question.title}`} disabled={disabled} placeholder={question.placeholder??'Write your answer…'} value={typeof selected==='string'&&!options.some(o=>o.value===selected)?selected:''} onChange={e=>answer(e.target.value)}/>}
    {error&&<p role="alert" className="text-destructive">{error}</p>}
    <div className="questionnaire-actions"><Button size="sm" variant="ghost" disabled={disabled||index===0} onClick={()=>navigate(index-1)}>Previous</Button>{question.optional&&<Button size="sm" variant="ghost" disabled={disabled} onClick={()=>advance(true)}>Skip</Button>}<Button size="sm" disabled={disabled} onClick={()=>advance()}>{index===visible.length-1?submitLabel:'Next'}</Button></div></section>;
}
